// At-rest encryption for everything the desk keeps in IndexedDB (plan
// "Offline", spec §11 "Storage & offline").
//
// The key is a NON-EXTRACTABLE AES-GCM-256 CryptoKey generated at sign-in and
// stored in IndexedDB as a structured-cloned handle — the raw key bytes never
// exist as JS-reachable memory, so a rogue extension reading IndexedDB, the
// DevTools Application tab, or a disk image of a stolen reception PC gets
// ciphertext plus an opaque handle that only this origin's live WebCrypto
// can use. It does NOT defeat script running inside a live, signed-in
// session (that script can call decrypt() like we do) — README.md says so.
//
// Rejected: a PBKDF2 key from the password (not retained after login) and a
// key derived from the JWT (rotates hourly, so yesterday's queue would become
// unreadable).

const ALGORITHM = 'AES-GCM';
const IV_BYTES = 12;

export interface SealedValue {
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
}

export function generateDeskKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: ALGORITHM, length: 256 }, false, ['encrypt', 'decrypt']);
}

/** `context` is bound as AES-GCM additional authenticated data: a record
 * only opens under the exact context it was sealed with (vault.ts uses
 * `name|owner|version`), so records can't be swapped between slots or
 * owners, and a tampered owner/version in `meta` makes every record fail. */
export async function sealJson(key: CryptoKey, value: unknown, context: string): Promise<SealedValue> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const additionalData = new TextEncoder().encode(context);
  const data = await crypto.subtle.encrypt({ name: ALGORITHM, iv, additionalData }, key, plaintext);
  return { iv, data };
}

export async function openJson<T>(key: CryptoKey, sealed: SealedValue, context: string): Promise<T> {
  const additionalData = new TextEncoder().encode(context);
  const plaintext = await crypto.subtle.decrypt({ name: ALGORITHM, iv: sealed.iv, additionalData }, key, sealed.data);
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}
